/**
 * APLICAR AS COTAS NOS CONTRATOS (09/10/2026): o plano, empresa a empresa, de levar cada assinatura a carregar a cota
 * acordada do rateio. Função pura; a tela só desenha e grava o que sai daqui.
 *
 * ⛔ O QUE O MOTOR EXIGE (backend, motor-do-rateio.util, "o balanço"). Por contrato, o motor tira TODOS os itens cujo
 * productId está no pool (`produtoIds`) e põe a cota por natureza; só aplica se a SOMA dos itens tirados for a cota, ao
 * centavo. Senão recusa ("o valor do item deixou de ser a cota acordada"). Depois da primeira divisão nenhum contrato
 * carrega a cota, e o motor recusava todas as faturas do grupo: a cota era digitada à mão, assinatura por assinatura.
 *
 * A REGRA, então: em cada empresa, Σ dos itens do pool do contrato = cota. A distribuição entre itens do pool dentro do
 * contrato não importa ao motor (todos saem), mas aqui só se escreve onde a escrita é INEQUÍVOCA:
 *  - um contrato ativo com exatamente um item do pool → esse item vale a cota;
 *  - um contrato ativo sem item do pool, e o pool com um produto só → entra um item desse produto, valendo a cota.
 * Todo o resto é RECUSA nomeada, com o que a pessoa teria de fazer na mão: dinheiro não se adivinha.
 */

export interface ItemDeContrato {
  productId?: number | null;
  productCode?: string | null;
  name?: string | null;
  description?: string | null;
  type?: string | null;
  quantity?: number | string | null;
  unitValue?: number | string | null;
  discount?: number | string | null;
  totalValue?: number | string | null;
  [k: string]: unknown;
}
export interface ContratoParaCota {
  id: number;
  clientId: number;
  status: string;
  mrr: number | string;
  items?: ItemDeContrato[] | null;
}
export interface LinhaParaCota {
  clientId: number;
  percentualVigente: number;
  valorAcordado: number | null;
  valorCobrado: number;
}
export interface RateioParaCota {
  matrizId: number;
  criterio: string;
  produtoIds: number[] | null;
  linhas: LinhaParaCota[];
  base: Array<{ clientId: number; ramais: number }>;
  itens: { vigente: Array<{ productId: number; productCode: string | null; name: string | null }> };
}

export type Mudanca =
  | { tipo: 'altera'; indice: number; produto: string; de: number; para: number }
  | { tipo: 'entra'; produto: string; para: number; descricao: string };

export type PlanoDaEmpresa =
  | { clientId: number; nome: string; situacao: 'ja-bate'; cota: number; contratoId: number | null }
  | { clientId: number; nome: string; situacao: 'recusa'; cota: number | null; contratoId: number | null; motivo: string }
  | {
      clientId: number; nome: string; situacao: 'aplica'; cota: number; contratoId: number;
      mudancas: Mudanca[]; itensNovos: ItemDeContrato[]; mrrAntes: number; mrrDepois: number; diferenca: number;
    };

const num = (v: unknown) => { const n = parseFloat(String(v ?? '')); return Number.isFinite(n) ? n : 0; };
const cent = (v: number) => Math.round(v * 100);
const brl = (v: number) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

/** A cota está no contrato? Σ itens do pool = acordado, ao centavo. É a condição do balanço do motor. */
export function precisaAplicar(rr: RateioParaCota): boolean {
  return rr.linhas.some((l) => l.valorAcordado != null && cent(l.valorCobrado) !== cent(l.valorAcordado));
}

export function planejarCotas(
  rr: RateioParaCota,
  contratos: ContratoParaCota[],
  nome: (clientId: number) => string,
): PlanoDaEmpresa[] {
  const pool = new Set((rr.produtoIds || []).map(Number));
  const produtoDoPool = (rr.produtoIds || []).length === 1 ? Number(rr.produtoIds![0]) : null;
  const ramaisDoGrupo = rr.base.reduce((t, b) => t + (Number(b.ramais) || 0), 0);

  return rr.linhas.map((l): PlanoDaEmpresa => {
    const n = nome(l.clientId);
    const ativos = contratos.filter((c) => c.clientId === l.clientId && c.status === 'Ativo');
    const recusa = (motivo: string, contratoId: number | null = null): PlanoDaEmpresa =>
      ({ clientId: l.clientId, nome: n, situacao: 'recusa', cota: l.valorAcordado, contratoId, motivo });

    if (l.valorAcordado == null) return recusa('A versão do rateio não registrou a cota em reais desta empresa: não há valor a aplicar.');
    const cota = l.valorAcordado;
    if (ativos.length === 0) return recusa(`${n} não tem assinatura ativa: a cota de ${brl(cota)} não tem onde entrar.`);
    if (ativos.length > 1) {
      return recusa(`${n} tem ${ativos.length} assinaturas ativas (${ativos.map((c) => `#${c.id}`).join(', ')}). O motor confere cada uma; ponha a cota de ${brl(cota)} na assinatura certa, na mão, e tire os itens do pool das outras.`);
    }
    const c = ativos[0];
    const itens = Array.isArray(c.items) ? c.items : [];
    const doPool = itens.map((it, indice) => ({ it, indice })).filter(({ it }) => it.productId != null && pool.has(Number(it.productId)));
    const somaPool = doPool.reduce((t, { it }) => t + num(it.totalValue), 0);

    // Dado da tela velho? O que o backend mede (valorCobrado) tem de ser o que este contrato soma.
    if (cent(somaPool) !== cent(l.valorCobrado)) {
      return recusa(`Os itens do pool na assinatura #${c.id} somam ${brl(somaPool)}, mas o rateio mede ${brl(l.valorCobrado)}. A tela está com dado velho: recarregue antes de aplicar.`, c.id);
    }
    if (cent(somaPool) === cent(cota)) return { clientId: l.clientId, nome: n, situacao: 'ja-bate', cota, contratoId: c.id };

    let itensNovos: ItemDeContrato[];
    let mudancas: Mudanca[];
    if (doPool.length === 1) {
      const { it, indice } = doPool[0];
      const q = num(it.quantity) || 1;
      if (num(it.discount) !== 0) {
        return recusa(`O item "${it.name}" da assinatura #${c.id} tem desconto: a cota não vira um valor unitário sem mexer no desconto. Ajuste na mão para o item somar ${brl(cota)}.`, c.id);
      }
      const unit = cota / q;
      if (cent(unit) * q !== cent(cota)) {
        return recusa(`O item "${it.name}" da assinatura #${c.id} tem quantidade ${q}, e ${brl(cota)} não se divide por ${q} em centavos. Ajuste na mão a quantidade ou o valor para o item somar ${brl(cota)}.`, c.id);
      }
      itensNovos = itens.map((x, i) => (i === indice ? { ...x, unitValue: cent(unit) / 100, totalValue: cota } : x));
      mudancas = [{ tipo: 'altera', indice, produto: String(it.name || it.productCode || `produto ${it.productId}`), de: num(it.totalValue), para: cota }];
    } else if (doPool.length === 0) {
      if (produtoDoPool == null) {
        return recusa(`A assinatura #${c.id} não tem item do que se divide, e o pool tem ${(rr.produtoIds || []).length} produtos: não dá para saber em qual a cota entra. Acrescente na mão o item que deve carregar ${brl(cota)}.`, c.id);
      }
      const ref = rr.itens.vigente.find((i) => Number(i.productId) === produtoDoPool)
        || contratos.flatMap((x) => x.items || []).find((i) => Number(i.productId) === produtoDoPool) as any;
      const produto = String(ref?.name || ref?.productCode || `produto ${produtoDoPool}`);
      const ramais = rr.base.find((b) => b.clientId === l.clientId)?.ramais;
      const fracao = rr.criterio === 'ramais_contratados' && ramais != null && ramaisDoGrupo > 0 ? ` (${ramais} de ${ramaisDoGrupo} ramais)` : '';
      // ⛔ A ORIGEM VAI NA DESCRIÇÃO: é o que o cliente lê na fatura antes de o motor rotular a linha.
      const descricao = `Cota de ${pct(l.percentualVigente)} da ${produto} compartilhada pelo grupo ${nome(rr.matrizId)}${fracao}`;
      itensNovos = [...itens, {
        productId: produtoDoPool,
        productCode: ref?.productCode || '',
        name: produto,
        description: descricao,
        type: 'MRR',
        quantity: 1,
        unitValue: cota,
        discount: 0,
        totalValue: cota,
      }];
      mudancas = [{ tipo: 'entra', produto, para: cota, descricao }];
    } else {
      return recusa(`A assinatura #${c.id} tem ${doPool.length} itens do que se divide (${doPool.map(({ it }) => `"${it.name}" ${brl(num(it.totalValue))}`).join(', ')}): não dá para saber como repartir a cota entre eles. Ajuste na mão para somarem ${brl(cota)}.`, c.id);
    }

    const mrrAntes = num(c.mrr);
    const diferenca = cota - somaPool;
    return { clientId: l.clientId, nome: n, situacao: 'aplica', cota, contratoId: c.id, mudancas, itensNovos, mrrAntes, mrrDepois: Math.round((mrrAntes + diferenca) * 100) / 100, diferenca: Math.round(diferenca * 100) / 100 };
  });
}
